import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getReservation } from "../api/services/reservationService";
import { normalizeReservation } from "../utils/reservationDetails";
import { getChildTypeFromCarClass } from "../utils/resultsPipeline";
import {
  Download,
  CheckCircle,
  Info,
  Phone,
  ArrowLeft,
  User,
  Briefcase,
  Snowflake,
  Zap,
  Fuel,
  CircleCheck,
  TriangleAlert,
} from "lucide-react";
import rentGurusLogo from "../images/rentGurusLogo.webp";
import { API_BASE_URL } from "../api";
import { GiCarDoor } from "react-icons/gi";
import { getVoucherRentalConditions } from "../api/services/rentalConditionService";
import { RentalConditionsModal } from "../components/RentalConditions";
import { CancellationTermsModal } from "../components/CancellationTerms";

export default function RentalVoucherPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSupplierTerms, setShowSupplierTerms] = useState(false);
  const [showCancellationTerms, setShowCancellationTerms] = useState(false);
  const [cancellationNotes, setCancellationNotes] = useState([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem("voucher");
    if (!stored) {
      navigate("/manage-booking");
      return;
    }
    const { emailId, reservationNumber } = JSON.parse(stored);

    async function load() {
      try {
        setLoading(true);
        const data = await getReservation({ emailId, reservationNumber });
        if (data) {
          setReservation(normalizeReservation(data));
          setCancellationNotes(data?.cancellation_policy?.notes || []);
        }
      } catch (err) {
        console.error("Voucher Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [navigate]);

  if (loading)
    return (
      <div className="flex h-screen items-center justify-center font-heading text-navy">
        Loading Voucher...
      </div>
    );
  if (!reservation)
    return (
      <div className="flex h-screen items-center justify-center font-heading text-navy">
        Reservation Not Found
      </div>
    );

  const { car, itinerary, customer, pricing, policy, vendor } = reservation;

  const handleDownloadPDF = async () => {
    if (downloading) return;
    try {
      setDownloading(true);
      const response = await fetch(
        `${API_BASE_URL}/reservation/${reservation.reservationNo}/voucher.pdf`,
        { method: "GET", credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `RentGurus-Voucher-${reservation.reservationNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF Download Error:", err);
      alert("Unable to download PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const isSameLocation =
    itinerary.pickupLocationCode === itinerary.dropOffLocationCode;

  return (
    <div className="min-h-screen bg-slate-50 py-4 md:py-8 px-3 sm:px-4 font-body print:bg-white print:p-0 mt-16">
      <style>
        {`
        @media print {
          .max-w-5xl {
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page { margin: 0.5cm; }
          .no-print { display: none !important; }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .rounded-xl, .border, .bg-dark { break-inside: avoid; }
        }

        /* Small device font scaling — only affects screens under 400px */
        @media (max-width: 399px) {
          .voucher-body p,
          .voucher-body span:not(.no-scale),
          .voucher-body div:not(.no-scale) {
            font-size: 11px;
          }
          .voucher-body .label-text {
            font-size: 9px;
          }
          .voucher-body .heading-text {
            font-size: 11px;
          }
        }
      `}
      </style>

      {/* ── BACK BUTTON ── */}
      <div className="max-w-4xl mx-auto py-4">
        <button
          onClick={() => window.close()}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-black font-heading font-semibold transition-colors"
        >
          <ArrowLeft size={15} /> Back to Manage Booking
        </button>
      </div>

      <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl print:rounded-none print:shadow-none">
        {/* ── HEADER ── */}
        <div className="bg-dark px-8 py-6 flex flex-row justify-between items-center">
          <div className="flex flex-col items-start gap-1">
            {/* <img
              src={rentGurusLogo}
              alt="RentGurus"
              className="h-8 sm:h-9 w-auto object-contain"
            /> */}
            <span className="font-heading font-extrabold text-2xl tracking-tight text-white">
              Rent<span className="text-primary">Gurus</span>
            </span>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white font-heading font-semibold">
              Rental Voucher
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1.5 font-heading">
              Reservation Number
            </p>
            <p className="font-heading text-xl sm:text-4xl font-black text-white">
              {reservation.reservationNo}
            </p>
          </div>
        </div>

        <div className="p-3 sm:p-5 md:p-8 space-y-4 md:space-y-6">
          {/* ── STATUS + DOWNLOAD ── */}
          <div className="flex items-center justify-between mb-4">
            <div className="inline-flex items-center gap-1.5 bg-primary/15 text-primary-hover text-[11px] font-bold px-3 py-1 rounded-full font-heading">
              <CheckCircle size={13} />
              {reservation.status || "CONFIRMED"}
            </div>
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="flex items-center gap-2 bg-black hover:bg-gray-800 text-white text-sm font-heading font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
            >
              <Download size={15} />
              {downloading ? "Downloading..." : "Download Voucher (PDF)"}
            </button>
          </div>

          {/* ── DRIVER + SUPPLIER ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            {/* Driver Details */}
            <div className="md:col-span-2 border border-gray-200 rounded-2xl p-5">
              <p className="font-heading text-xs font-bold uppercase tracking-widest text-slate-700 mb-4 pb-3 border-b border-gray-100">
                Driver Details
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div>
                  <p className="text-[10px] font-bold text-gray-500 tracking-wide uppercase mb-1 font-heading">
                    Driver Name
                  </p>
                  <p className="font-heading font-semibold text-gray-900">
                    {customer.firstName} {customer.lastName}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-500 tracking-wide uppercase mb-1 font-heading">
                    Date of Birth
                  </p>
                  <p className="font-heading font-semibold text-gray-900">
                    {new Date(customer.dob).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-500 tracking-wide uppercase mb-1 font-heading">
                    Email
                  </p>
                  <p className="font-heading font-semibold text-gray-900 break-all">
                    {customer.email}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-500 tracking-wide uppercase mb-1 font-heading">
                    Contact Number
                  </p>
                  <p className="font-heading font-semibold text-gray-900">
                    {customer.phone}
                  </p>
                </div>
                {customer.residency && (
                  <div>
                    <p className="text-[10px] font-bold text-gray-500 tracking-wide uppercase mb-1 font-heading">
                      Residency
                    </p>
                    <p className="font-heading font-semibold text-gray-900">
                      {customer.residency}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Rental Provider */}
            <div className="border border-gray-200 rounded-2xl p-5 flex flex-col items-center text-center gap-3">
              <p className="font-heading text-[10px] text-slate-700 font-bold uppercase tracking-widest text-gray-400">
                Rental Provider
              </p>
              <img
                src={vendor.logo}
                alt="provider"
                className="h-10 w-auto object-contain"
              />
              <div>
                <p className="font-heading text-sm font-bold text-gray-900">
                  {vendor.name}
                </p>
                <p className="font-heading text-[11px] text-gray-400 mt-0.5">
                  Supplier No: {reservation.vendorReservationNo}
                </p>
              </div>
            </div>
          </div>

          {/* ── VEHICLE INFO ── */}
          <div className="rounded-xl border border-slate-200 p-4 sm:p-6 shadow-sm">
            <h2 className="mb-4 font-heading text-xs font-bold uppercase tracking-widest text-slate-700 border-b pb-2">
              Vehicle Information
            </h2>
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
              <img
                src={car.carImage}
                alt="vehicle"
                className="w-36 sm:w-40 md:w-48 object-contain soft-edge-mask flex-shrink-0"
              />
              <div className="space-y-2 w-full text-center sm:text-left">
                <p className="font-heading text-lg sm:text-xl font-extrabold text-navy leading-tight">
                  {car.makeAndModel}{" "}
                  {!car.isExactModel && (
                    <span className="text-sm font-normal text-slate-500">
                      or similar
                    </span>
                  )}
                </p>
                <p className="font-heading text-xs font-semibold text-primary uppercase tracking-wider">
                  {getChildTypeFromCarClass(car.sippCode)}
                </p>
                <div className="font-body font-semibold flex flex-wrap items-center gap-x-4 gap-y-2 text-text pt-1 justify-center sm:justify-start">
                  <span className="flex items-center gap-1 text-xs">
                    <GiCarDoor className="w-4 h-4 text-primary" /> {car.doors} Doors
                  </span>
                  <span className="flex items-center gap-1 text-xs">
                    <User className="w-4 h-4 text-primary" /> {car.seats} Seats
                  </span>
                  <span className="flex items-center gap-1 text-xs">
                    <Briefcase className="w-4 h-4 text-primary" /> {car.bags} Bags
                  </span>
                  <span className="flex items-center gap-1 text-xs">
                    <Snowflake className="w-4 h-4 text-primary" />{" "}
                    {car.airConditioning ? "AC" : "Non-AC"}
                  </span>
                  <span className="flex items-center gap-1 text-xs capitalize">
                    <Zap className="w-4 h-4 text-primary" />{" "}
                    {car.transmission === "A" ? "Automatic" : "Manual"}
                  </span>
                  {car.fuelPolicy && (
                    <span className="flex items-center gap-1 text-xs">
                      <Fuel className="w-4 h-4 text-primary" />
                      {car.fuelPolicy === "FULL-FULL"
                        ? "Full to Full"
                        : car.fuelPolicy}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── RENTAL INCLUSIONS ── */}
          {policy.inclusions?.length > 0 && (
            <div className="rounded-xl border border-slate-200 p-4 sm:p-6">
              <h2 className="mb-4 font-heading text-xs font-bold uppercase tracking-widest text-slate-700 border-b pb-2">
                Rental Inclusions
              </h2>
              <div className="flex flex-wrap gap-x-4 sm:gap-x-6 gap-y-2">
                {policy.inclusions.map((item, i) => (
                  <div
                    key={i}
                    className="font-heading flex items-start gap-2 text-xs text-gray-600"
                  >
                    <CircleCheck
                      size={14}
                      className="mt-0.5 text-emerald-500 flex-shrink-0"
                    />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── PICKUP & DROPOFF BAR ── */}
          <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
              <div className="p-4 sm:p-5">
                <p className="font-heading text-xs font-bold text-primary uppercase tracking-widest mb-2">
                  Pick-up Details
                </p>
                <p className="font-heading text-sm font-bold text-navy">
                  {itinerary.pickupLocationName}
                </p>
                <p className="font-heading text-sm text-gray pt-1">
                  {new Date(itinerary.pickupDateTime).toLocaleDateString(
                    "en-GB",
                    { day: "2-digit", month: "short", year: "numeric" },
                  )}
                  ,{" "}
                  <span className="font-heading text-navy font-bold">
                    {new Date(itinerary.pickupDateTime).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </span>
                </p>
              </div>
              <div className="p-4 sm:p-5">
                <p className="font-heading text-xs font-bold text-primary uppercase tracking-widest mb-2">
                  Drop-off Details
                </p>
                <p className="font-heading text-sm font-bold text-navy">
                  {isSameLocation
                    ? itinerary.pickupLocationName
                    : itinerary.dropOffLocationName}
                </p>
                <p className="font-heading text-sm text-gray pt-1">
                  {new Date(itinerary.dropOffDateTime).toLocaleDateString(
                    "en-GB",
                    { day: "2-digit", month: "short", year: "numeric" },
                  )}
                  ,{" "}
                  <span className="font-heading text-navy font-bold">
                    {new Date(itinerary.dropOffDateTime).toLocaleTimeString(
                      [],
                      { hour: "2-digit", minute: "2-digit", hour12: false },
                    )}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* ── LOCATION & HOURS ── */}
          <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-4 sm:px-6 py-3 border-b border-slate-200">
              <h2 className="font-heading text-xs font-bold uppercase tracking-widest text-slate-700">
                Pick-up / Drop-off Address & Operating Hours
              </h2>
            </div>

            {isSameLocation ? (
              <div className="p-4 sm:p-5">
                <div className="grid grid-cols-1 gap-y-3 md:grid-cols-[140px_1fr] md:gap-y-4 text-sm">
                  <div className="font-heading text-[11px] font-bold uppercase text-gray-500">
                    Location
                  </div>
                  <div className="font-heading font-bold text-navy">
                    {itinerary.pickupLocationName} (
                    {itinerary.pickupLocationCode})
                  </div>

                  <div className="font-heading text-[11px] font-bold uppercase text-gray-500">
                    Address
                  </div>
                  <div className="font-heading text-xs leading-relaxed text-gray-600">
                    {itinerary.pickupAddress}
                  </div>

                  <div className="font-heading text-[11px] font-bold uppercase text-gray-500">
                    Contact
                  </div>
                  <div className="font-heading text-xs font-bold text-navy">
                    {itinerary.pickupContact || "N/A"}
                  </div>

                  <div className="font-heading text-[11px] font-bold uppercase text-gray-500">
                    Open Hours
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-x-3 sm:gap-x-6 gap-y-2">
                      {["mon", "tue", "wed", "thu"].map(
                        (day) =>
                          itinerary.pickupHours?.[day] && (
                            <div
                              key={day}
                              className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100"
                            >
                              <span className="text-[9px] font-black uppercase text-primary w-6">
                                {day}:
                              </span>
                              <span className="text-[10px] font-semibold text-navy whitespace-nowrap">
                                {Array.isArray(itinerary.pickupHours[day])
                                  ? itinerary.pickupHours[day].join(", ")
                                  : itinerary.pickupHours[day]}
                              </span>
                            </div>
                          ),
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 sm:gap-x-6 gap-y-2">
                      {["fri", "sat", "sun"].map(
                        (day) =>
                          itinerary.pickupHours?.[day] && (
                            <div
                              key={day}
                              className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100"
                            >
                              <span className="text-[9px] font-black uppercase text-primary w-6">
                                {day}:
                              </span>
                              <span className="text-[10px] font-semibold text-navy whitespace-nowrap">
                                {Array.isArray(itinerary.pickupHours[day])
                                  ? itinerary.pickupHours[day].join(", ")
                                  : itinerary.pickupHours[day]}
                              </span>
                            </div>
                          ),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                {/* PICKUP */}
                <div className="p-4 sm:p-6 space-y-3">
                  <p className="text-[10px] font-bold text-gold uppercase tracking-wider">
                    Pick-up Location
                  </p>
                  <p className="text-sm font-bold text-navy">
                    {itinerary.pickupLocationName}
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {itinerary.pickupAddress}
                  </p>
                  <p className="text-xs font-bold text-navy">
                    <Phone size={12} className="inline mr-1 text-gold" />
                    {itinerary.pickupContact || "N/A"}
                  </p>
                  <div className="bg-slate-50/50 p-3 rounded-lg border border-slate-100 grid grid-cols-2 gap-x-4 gap-y-1 text-[9px]">
                    {Object.entries(itinerary.pickupHours || {}).map(
                      ([day, times]) => (
                        <div
                          key={day}
                          className="flex justify-between border-b border-slate-200/50 py-1"
                        >
                          <span className="uppercase font-bold text-gray-400">
                            {day}:
                          </span>
                          <span className="text-navy font-semibold">
                            {Array.isArray(times) ? times.join("-") : times}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
                {/* DROPOFF */}
                <div className="p-4 sm:p-6 space-y-3">
                  <p className="text-[10px] font-bold text-gold uppercase tracking-wider">
                    Drop-off Location
                  </p>
                  <p className="text-sm font-bold text-navy">
                    {itinerary.dropOffLocationName}
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {itinerary.dropOffAddress}
                  </p>
                  <p className="text-xs font-bold text-navy">
                    <Phone size={12} className="inline mr-1 text-gold" />
                    {itinerary.dropOffContact || "N/A"}
                  </p>
                  <div className="bg-slate-50/50 p-3 rounded-lg border border-slate-100 grid grid-cols-2 gap-x-4 gap-y-1 text-[9px]">
                    {Object.entries(itinerary.dropOffHours || {}).map(
                      ([day, times]) => (
                        <div
                          key={day}
                          className="flex justify-between border-b border-slate-200/50 py-1"
                        >
                          <span className="uppercase font-bold text-gray-400">
                            {day}:
                          </span>
                          <span className="text-navy font-semibold">
                            {Array.isArray(times) ? times.join("-") : times}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── PAYMENT SUMMARY ── */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-primary/20">
              <h2 className="font-heading text-xs font-bold uppercase tracking-widest text-gray-500">
                Payment Summary
              </h2>
            </div>
            <div className="px-4 sm:px-6 py-4 space-y-0">
              <div className="flex justify-between items-center py-2 border-b border-primary/15 text-sm">
                <span className="font-heading text-gray-500">Paid Online</span>
                <span className="font-heading font-bold text-navy">
                  {pricing.currency} {Number(pricing.payNow).toFixed(2)}
                </span>
              </div>

              {pricing.extrasAmount > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-primary/15 text-sm">
                  <span className="font-heading text-gray-500">
                    Selected Extras
                  </span>
                  <span className="font-heading font-bold text-navy">
                    {pricing.currency} {Number(pricing.extrasAmount).toFixed(2)}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center py-2 border-b border-primary/15 text-sm">
                <span className="font-heading text-gray-500">
                  Payable at Pick-up
                  {pricing.extrasAmount > 0 ? " (incl. extras)" : ""}
                </span>
                <span className="font-heading font-bold text-navy">
                  {pricing.currency} {Number(pricing.payAtDesk).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between items-center pt-3">
                <span className="font-heading text-sm font-bold text-navy tracking-wide">
                  Total Price
                </span>
                <span className="font-heading text-xl sm:text-2xl font-black text-navy">
                  {pricing.currency}{" "}
                  {Number(pricing.totalRentalPrice).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* ── WARNING BOX ── */}
          <div className="rounded-lg bg-blue-50 p-3 border border-blue-100 flex gap-3 items-start no-print">
            <TriangleAlert
              size={16}
              className="text-blue-600 flex-shrink-0 mt-1"
            />
            <div className="font-heading text-sm text-navy leading-relaxed">
              <strong>Mandatory:</strong> You must present a physical Credit
              Card in the main driver's name at the desk. Digital/Debit cards
              may result in booking refusal.
            </div>
          </div>

          {/* ── TERMS LINE ── */}
          <p className="font-heading text-xs text-gray-500 text-center no-print">
            Click here to view{" "}
            <button
              type="button"
              onClick={() => setShowSupplierTerms(true)}
              className="font-heading text-primary font-bold hover:underline uppercase transition-colors"
            >
              Supplier Rental Terms
            </button>{" "}
            ,{" "}
            <button
              type="button"
              onClick={() =>
                window.open(
                  "/terms-and-conditions",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="font-heading text-primary font-bold hover:underline uppercase transition-colors"
            >
              Booking Terms &amp; Conditions
            </button>{" "}
            and{" "}
            <button
              type="button"
              onClick={() => setShowCancellationTerms(true)}
              className="font-heading text-primary font-bold hover:underline uppercase transition-colors"
            >
              Cancellation Terms
            </button>
          </p>
        </div>
      </div>
      <RentalConditionsModal
        isOpen={showSupplierTerms}
        onClose={() => setShowSupplierTerms(false)}
        reservationNo={reservation.reservationNo}
      />

      <CancellationTermsModal
        isOpen={showCancellationTerms}
        onClose={() => setShowCancellationTerms(false)}
        policies={cancellationNotes}
      />

      {/* ── ACTION BUTTONS ── */}
      <div className="mx-auto max-w-5xl mt-6 sm:mt-8 flex justify-center gap-4 no-print">
        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="flex items-center gap-2 rounded-full bg-primary px-6 sm:px-8 py-3 sm:py-4 text-sm font-bold text-white shadow-premium transition-all hover:bg-primary-hover active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {downloading ? (
            <>
              <svg
                className="animate-spin w-4 h-4 text-white flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Downloading...
            </>
          ) : (
            <>
              <Download size={18} className="text-white" />
              Download Rental Voucher
            </>
          )}
        </button>
      </div>
    </div>
  );
}
